import React, { useEffect, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { isSuperAdmin } from "../shared/MaintenanceGuard";
import { OcpiPermission, RegistrationState } from "../../../api/maintenance/ocpi";

/** Only super-admins change OCPI configuration; maintenance-viewer is read-only. */
export const canEditOcpi = (): boolean => isSuperAdmin();

/** Backend detail may be a string or an object ({message, ...}). */
export function errText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg || String(x)).join("; ");
  if (typeof d === "object" && d.message) return d.message;
  return fallback;
}

/** Timestamps arrive as UTC ISO with an offset; always render in the viewer's zone. */
export function fmtTs(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function ago(iso?: string | null): string {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export const StatePill: React.FC<{ state: RegistrationState; suspended?: boolean }> = ({ state, suspended }) => {
  if (suspended) return <span className="ma-pill bad">Suspended</span>;
  const cls = { REGISTERED: "ok", PENDING: "warn", NEW: "info", UNREGISTERED: "muted" }[state] || "muted";
  const label = { REGISTERED: "Connected", PENDING: "Awaiting partner", NEW: "No key yet", UNREGISTERED: "Disconnected" }[state] || state;
  return <span className={`ma-pill ${cls}`}>{label}</span>;
};

export const CopyBtn: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="maint-btn-small"
      aria-label={label || "Copy to clipboard"}
      title={label || "Copy"}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable (insecure context) — the value is selectable */
        }
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

/** Modal dialog: Escape closes, focus moves in on open and back on close, backdrop click closes. */
export const Modal: React.FC<{ title: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({
  title, onClose, wide, children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input, select, textarea, button:not([data-close])");
    first?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); previous?.focus?.(); };
  }, [onClose]);
  return (
    <div className="maint-form-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={`maint-form-card ${wide ? "maint-form-card--widest" : "maint-form-card--wide"}`}
           role="dialog" aria-modal="true" aria-label={title}>
        <div className="maint-form-header">
          <h3>{title}</h3>
          <button type="button" className="maint-btn-icon" onClick={onClose} aria-label="Close" data-close><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

/** Group the permission catalog by module, preserving catalog order. */
export function groupPermissions(catalog: OcpiPermission[]): { module: string; items: OcpiPermission[] }[] {
  const groups: { module: string; items: OcpiPermission[] }[] = [];
  for (const p of catalog) {
    let g = groups.find((x) => x.module === p.module);
    if (!g) { g = { module: p.module, items: [] }; groups.push(g); }
    g.items.push(p);
  }
  return groups;
}

export const MODULE_LABEL: Record<string, string> = {
  locations: "Locations", tariffs: "Tariffs", sessions: "Sessions", cdrs: "CDRs (billing)", tokens: "Driver tokens",
  commands: "Remote commands", chargingprofiles: "Smart charging", hubclientinfo: "Hub client info",
};

export const PARTY_ROLE_LABEL: Record<string, string> = {
  EMSP: "eMSP", NSP: "Navigation", HUB: "Hub", NAP: "National access point", SCSP: "Smart charging", OTHER: "Other", CPO: "CPO",
};
